import React, { useState } from 'react'

import { Link as RouterLink } from 'react-router-dom'

import { Box, Popover, Stack, Typography, useTheme } from '@mui/material'

import { Button, MENU_PAPER_SX, menuPaperChromeSx } from '@repo/ui'

// Direct file import rather than the `components/display` barrel: the barrel pulls in every
// display component, so a single one mid-edit takes this file's compile with it. The page
// this replaces imported the indicator by path for the same reason.
import { InvalidScanIndicator } from 'components/display/InvalidScanIndicator'
import { summarizeInvalidScans } from 'utils/invalidScanSeverity'

/**
 * VRG-491 — the invalid-scan indicator, made into a control.
 *
 * ## Why the ACTION lives here, and now lives ONLY here
 *
 * "Acknowledge invalid scans" used to exist only as a row in the page's overflow menu,
 * which puts the action nowhere near the thing it acts on: the COUNT is rendered in the
 * header band, and the control that clears it was three surfaces away in a list of ten
 * unrelated verbs. Co-locating them means the reader who notices the number can resolve
 * it from where they noticed it.
 *
 * ⚠️ THIS PANEL IS THE SOLE PATH TO THE ACTION. When it was first built the kebab row was
 * kept alongside it, on the argument that someone who has learned record actions live in
 * the overflow menu should not ALSO have to discover that a status glyph is clickable.
 * The user removed the kebab row on 2026-08-31, knowing that argument and accepting its
 * cost: the indicator only reveals its clickability on hover, so at rest it still reads
 * as a badge rather than a control, and discoverability is now a single point of failure
 * rather than an enhancement. One path was preferred to two.
 *
 * The consequence to hold onto: every guarantee the kebab row used to provide is now this
 * component's alone. In particular the §46 permission tooltip below is the ONLY place a
 * user without `certificate_acknowledge_invalid_scans` is told the capability exists — so
 * if that ever moves, it moves nowhere, it just disappears. If discoverability does prove
 * to be the problem, make the INDICATOR read as a control at rest; do not quietly restore
 * a menu row three surfaces from the count it acts on (`CertificatePage.tsx` carries the
 * matching note where that row stood).
 *
 * ## Why a POPOVER and not a tooltip — do not "simplify" this back
 *
 * A tooltip was the first instinct and loses on four independent grounds, any one of
 * which is disqualifying:
 *
 *  1. **Hover-transience.** Crossing the gap from trigger into tooltip to press a button
 *     is a race against the leave-delay. Making it survivable means reimplementing
 *     hover-intent for a one-off surface — `hooks/useHoverIntent.ts` exists precisely
 *     because that is not a small thing.
 *  2. **No tab stop.** A tooltip is not focusable, so the action inside it would be
 *     mouse-only. Acknowledging is a real mutation; it cannot be mouse-only.
 *  3. **The trigger already had a click meaning.** It navigated to the filtered Scans
 *     tab. One target cannot cleanly host a navigation on click and a different action on
 *     hover. (That navigation is not deleted — it MOVED, into this panel, as the "View
 *     invalid scans" button. Still a real anchor, via `component={RouterLink}`: the
 *     element changed, the link semantics did not.)
 *  4. **§46 requires a gated control to carry a tooltip naming the missing permission.**
 *     Inside a tooltip that is a tooltip nested in a tooltip.
 *
 * The indicator's own hover tooltip still exists and still explains what happened — it is
 * suppressed only while this popover is open, so the two never stack.
 */

/**
 * Small integers as words, because the panel's first line is a SENTENCE and a sentence
 * opening with a numeral reads as a data point rather than a statement. Above twelve the
 * word is longer to read than the numeral, which is the usual style cutoff, so digits
 * take over.
 *
 * Deliberately local: this is the only surface that needs it, and hoisting a
 * general-purpose number-speller into `utils` on one call site would be inventing a
 * shared API from a sample of one (§36 — extract on the SECOND hand-roll, not the first).
 */
const SMALL_NUMBER_WORDS = [
  'Zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
] as const

const countInWords = (n: number): string =>
  n >= 0 && n < SMALL_NUMBER_WORDS.length ? SMALL_NUMBER_WORDS[n] : String(n)

export interface InvalidScanPopoverProps {
  /** `invalid_scan_count` from the invalid-scans payload. */
  count?: number | null
  /** `invalid_reason_counts` from the same payload. */
  reasonCounts?: Record<string, number> | null
  /** Glyph size in px, forwarded to the indicator. */
  size?: number
  /** Route for "View invalid scans" — the Scans tab pre-filtered to invalid. */
  viewScansTo: string
  /** Opens the acknowledge confirmation. The gate itself lives at the call site. */
  onAcknowledge: () => void
  /**
   * §46 — whether the user holds `certificate_acknowledge_invalid_scans`. A company admin
   * CAN grant it, so lacking it DISABLES the button with a reason; it is never hidden.
   */
  canAcknowledge: boolean
  /** True while the acknowledge mutation is in flight (§46 category 3). */
  isAcknowledging?: boolean
}

export const InvalidScanPopover = ({
  count,
  reasonCounts,
  size = 16,
  viewScansTo,
  onAcknowledge,
  canAcknowledge,
  isAcknowledging = false,
}: InvalidScanPopoverProps) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const summary = summarizeInvalidScans(reasonCounts, count)

  // Same guard the indicator applies. Kept here too so the popover's own state and
  // handlers are never built for a certificate with nothing to show.
  if (!summary) return null

  const open = Boolean(anchorEl)
  const isSpoof = summary.severity === 'spoof'
  const plural = summary.count === 1 ? 'scan' : 'scans'

  const close = () => setAnchorEl(null)

  return (
    <>
      <InvalidScanIndicator
        size={size}
        count={count}
        reasonCounts={reasonCounts}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        // The visible content is a glyph and a bare number, so the accessible name has to
        // supply the noun. Without it this announces as "3, button".
        ariaLabel={`${summary.count} invalid ${plural}`}
        ariaHasPopup="dialog"
        ariaExpanded={open}
        // One floating surface at a time: the popover restates the tooltip's content and
        // adds the actions, so a tooltip alongside it is redundant and overlaps it.
        suppressTooltip={open}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              /* working-conventions §69 — BOTH, and in this order.
                 `MENU_PAPER_SX` is SPACING only (the 8px trigger gap, insets, max
                 height). `menuPaperChromeSx` is the SURFACE, and it is the half that
                 actually matters here: a `Popover` is not a `MuiMenu`, so the theme's
                 `MuiMenu.paper` override — including the load-bearing
                 `backgroundImage: 'none'` that kills MUI's dark-mode elevation
                 overlay — never reaches it. Omit the chrome and this panel renders a
                 pale grey against a near-black page, which is the exact defect §69
                 was written for and which has shipped in this repo more than once.
                 Deliberate overrides go AFTER the spreads; spread order is
                 load-bearing. */
              ...MENU_PAPER_SX,
              ...menuPaperChromeSx(theme),
              // Matches the indicator tooltip's own 340px, so the two surfaces the same
              // trigger can produce are the same width and the copy does not re-wrap
              // between them.
              width: 340,
              maxWidth: '90vw',
              p: 4,
            },
          },
        }}
      >
        {/* `dialog` + a label, because `aria-haspopup="dialog"` on the trigger promises
            one. The heading carries the name rather than a duplicated `aria-label`. */}
        <Box role="dialog" aria-labelledby="invalid-scan-popover-title">
          <Typography
            id="invalid-scan-popover-title"
            variant="body2"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            {countInWords(summary.count)} {plural} failed verification
          </Typography>

          {/* ⚠️ `component="p"` is REQUIRED on every `body3` here, and is not cosmetic.
              `body3` is a custom variant (declarations/mui.d.ts); MUI's `variantMapping`
              has no entry for it, so Typography falls back to rendering a `<span>` —
              `display: inline`. On an inline box a vertical margin does nothing, so the
              `mt` below computed to 0px and these two paragraphs ran together as one
              line ("…a proof was replayed.Most common reason: …"). Measured in the
              browser, not inferred. Same failure §31 records for fields: the spacing was
              being written somewhere it could not apply. */}
          <Typography
            variant="body3"
            component="p"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            {isSpoof
              ? 'A tag was presented that we did not issue, or a proof was replayed.'
              : 'Most likely an old or mangled link rather than a forgery.'}
          </Typography>

          <Typography
            variant="body3"
            component="p"
            color="text.secondary"
            sx={{ lineHeight: 1.5, mt: 2 }}
          >
            {/* The label recedes; the reason itself is the actionable half and stays at
                full strength. Mirrors the indicator tooltip's own treatment. */}
            <Box component="span" sx={{ opacity: 0.7 }}>
              Most common reason:{' '}
            </Box>
            {summary.topReasonLabel}.
          </Typography>

          {/* TWO BUTTONS, and the ORDER is the house rule rather than the order they were
              written in: exactly one PRIMARY and it is RIGHTMOST (§26/§33). Acknowledge is
              the action this panel exists for — clicking the indicator is how you reach it
              at all now that the kebab row is gone — so it takes the primary and the right
              edge. "View invalid scans" is the escape hatch for someone who wants to read
              the rows before deciding, so it is SECONDARY and sits to its left.

              That rule is written for a page header, but the reasoning transfers intact: a
              predictable right edge for the CTA is what lets someone act without reading
              the group, and it is worth more inside a 340px panel than on a wide header
              where the buttons are far apart anyway.

              12px between them (§29 button-group gap, `spacing={3}` on the platform's 4px
              base). Both are the DEFAULT 40px — never `size="small"` (§60).

              `component={RouterLink}` keeps the navigation a REAL ANCHOR — right-click,
              middle-click, cmd-click and the status-bar preview all survive, which an
              `onClick` calling `navigate()` would silently lose. This was the indicator's
              own click behaviour before the popover existed; it moved in here rather than
              being dropped, and it must not degrade into a handler now that it wears
              button chrome.

              NO REFERRER (§42), and that is correct rather than an omission: this goes to
              the Scans TAB of the certificate already on screen, which is same-record
              wayfinding, not the lateral record-to-record jump §42 governs — its own
              carve-out names tabs explicitly.

              No trailing arrow glyph. The link needed one to read as a link inside body
              copy; a bordered button already carries that affordance, and an arrow inside
              a button reads as "next step" rather than "goes elsewhere".

              `useFlexGap` + `flexWrap` are DEFENSIVE, not decorative, and the numbers are
              why. Measured in the browser at 1280x800: the two buttons occupy 160.3 + 12 +
              128.8 = 301.1px inside a 340px panel with 16px padding, i.e. 308px of content
              box — 5.9px of slack. That is real but thin, and both buttons carry MUI's
              `min-width: fit-content`, so a font that renders a hair wider (a different
              platform, a fallback face before the webfont lands) would not shrink them: the
              row would push past the panel's padding and clip. Wrapping is the graceful
              version of that failure. `useFlexGap` is required for `spacing` to survive
              wrapping — Stack implements `spacing` as child margins by default, which a
              wrapped row applies in the wrong axis; with it, the 12px is a real `gap` and
              holds in both directions. Re-measure if either label changes. */}
          <Stack
            direction="row"
            spacing={3}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ mt: 5 }}
          >
            <Button
              variant="secondary"
              component={RouterLink}
              to={viewScansTo}
              onClick={close}
            >
              View invalid {plural}
            </Button>

            <Button
              variant="primary"
              onClick={() => {
                // Close first: the confirmation is a modal, and leaving a popover open
                // underneath it means dismissing the modal reveals a stale panel over
                // content the user has already moved past.
                close()
                onAcknowledge()
              }}
              disabled={!canAcknowledge || isAcknowledging}
              /* §46 — a disabled control MUST say why. `disabledReason` is the shared
                 Button's own mechanism for this: it swaps the native `disabled`
                 attribute for `aria-disabled` so the element still receives hover and
                 focus events (a natively-disabled button fires neither, which is how a
                 disabled control ends up silently swallowing the click it was meant to
                 explain). Wording matches the confirm modal verbatim — and this is now
                 the ONLY surface that carries it, so it is the only place a user without
                 the grant is told the capability exists. */
              disabledReason={
                !canAcknowledge
                  ? "You need the 'Acknowledge scans' permission. Ask an admin to grant it."
                  : isAcknowledging
                    ? 'Acknowledging…'
                    : undefined
              }
            >
              Acknowledge
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  )
}
